import type { ComponentProps, FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'ink' | 'brand' | 'paper' | 'outline' | 'outline-light'

export type ButtonSize = 'sm' | 'md' | 'lg'

/** Shared pill button styles, used by ButtonLink and by plain <button> elements. */
export const buttonStyles = (variant: ButtonVariant = 'ink', size: ButtonSize = 'md'): string => {
    return cn(
        'group/button inline-flex shrink-0 items-center justify-center gap-2 rounded-full font-medium tracking-[-0.01em] whitespace-nowrap transition-[background-color,color,border-color,transform] duration-300 ease-out active:scale-[0.98] disabled:pointer-events-none disabled:opacity-60',
        size === 'sm' && 'h-10 px-4 text-sm',
        size === 'md' && 'h-12 px-6 text-[15px]',
        size === 'lg' && 'h-14 px-7 text-base',
        variant === 'ink' && 'bg-foreground text-background hover:bg-brand hover:text-brand-foreground',
        variant === 'brand' && 'bg-brand text-brand-foreground hover:bg-foreground hover:text-background',
        variant === 'paper' && 'bg-background text-foreground hover:bg-brand-light',
        variant === 'outline' && 'border border-foreground/25 text-foreground hover:border-foreground hover:bg-foreground hover:text-background',
        variant === 'outline-light' && 'border border-background/30 text-background hover:border-background hover:bg-background hover:text-foreground'
    )
}

export const ButtonArrow: FC<{ className?: string }> = ({ className }): ReactNode => {
    return (
        <ArrowUpRight className={cn('size-4 transition-transform duration-300 group-hover/button:rotate-45', className)} />
    )
}

interface ButtonLinkProps extends Omit<ComponentProps<typeof Link>, 'className'> {
    variant?: ButtonVariant
    size?: ButtonSize
    icon?: ReactNode
    className?: string
}

export const ButtonLink: FC<ButtonLinkProps> = ({
    variant = 'ink',
    size = 'md',
    icon = <ButtonArrow />,
    className,
    children,
    ...props
}): ReactNode => {
    return (
        <Link className={cn(buttonStyles(variant, size), className)} {...props}>
            {children}
            {icon}
        </Link>
    )
}
